import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  createContainerFile,
  deleteContainerFile,
  getContainerSKURecords,
} from "api/purchase";
import { ConfirmDialog, Spacer } from "components/Custom";
import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { toBase64Handler } from "utils/base64";
import { timezoneFormat } from "utils/date";
import { sliceText } from "utils/string";

import {
  Calendar,
  Close,
  ExpandMore,
  ExternalLink,
  Pencil,
  Trash,
} from "icons";

import * as XLSX from "xlsx";

import useStyles from "../containers/drawerStyle";

let intervalID;

const DeleteFileModal = ({ file_id, onClose, onDelete }) => {
  const layoutDispatch = useLayoutDispatch();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <ConfirmDialog
      open={isLoading || !!file_id}
      title="Delete File"
      children="Container file will be deleted?"
      confirmText="Delete"
      onConfirm={() => {
        setIsLoading(true);
        deleteContainerFile({
          id: file_id,
          responseSetter: (res) => {
            setIsLoading(false);
            if (res.success) {
              onClose(true);
              onDelete();
              popupNotification({
                dispatch: layoutDispatch,
                status: "success",
                message: `Container file deleted.`,
              });
            } else {
              if (res.message) {
                popupNotification({
                  dispatch: layoutDispatch,
                  status: "error",
                  message: res.message,
                });
              }
            }
          },
        });
      }}
      isLoading={isLoading}
      onClose={() => onClose(false)}
    />
  );
};

export default function ContainerDrawer({
  open,
  onClose,
  container,
  onEdit,
  onFileDelete,
}) {
  const [details, setDetails] = useState(null);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (open && container) {
      setIsProductsLoading(true);
      getContainerSKURecords({
        params: { container_id: container.id, limit: 9999 },
        responseSetter: (res) => {
          setIsProductsLoading(false);
          if (res?.count) {
            setDetails(res?.results || []);
          } else {
            setDetails([]);
          }
        },
      });
    }
  }, [open, container]);

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      PaperProps={{
        className: classes.drawerPaper,
      }}
    >
      <div className={classes.root}>
        <div className={classes.title}>
          <div
            style={{
              paddingLeft: "1.5rem",
            }}
          >
            <Typography variant="span">{`${container.container_number}`}</Typography>
            {container.scheduled_date && (
              <Grid container alignItems="center" justifyContent="center">
                <Grid item className={classes.calendarIcon}>
                  <Calendar size={0.7} color="white" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="white">
                    {container.scheduled_date}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </div>
          <Tooltip title={"Edit"} aria-label="edit-button">
            <IconButton
              disableRipple={true}
              onClick={() => onEdit(container)}
              className={classes.purple}
              style={{ marginLeft: "auto" }}
            >
              <Pencil color="#FFF" />
            </IconButton>
          </Tooltip>

          <IconButton
            disableRipple={true}
            onClick={() => onClose(false)}
            className={classes.orange}
          >
            <Close color="#FFF" />
          </IconButton>
        </div>
        <FilesAccordion
          container={container}
          onClose={onClose}
          onFileDelete={onFileDelete}
        />
        <ContainerAccordion container={container} />
        <ProductsAccordion
          details={details}
          isProductsLoading={isProductsLoading}
        />
      </div>
    </Drawer>
  );
}

const LineItem = ({ title, value, classes }) => (
  <Grid item xs={6}>
    <Grid container spacing={1}>
      <Grid item>
        <Typography
          variant="subtitle2"
          color="textPrimary"
          className={classes.lineItemTitle}
        >
          {title}
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant="body2"
          color="textPrimary"
          className={classes.lineItemValue}
        >
          {value || "-"}
        </Typography>
      </Grid>
    </Grid>
  </Grid>
);

function ContainerAccordion({ container }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.accordion}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
      >
        <Grid container alignItems="center">
          <Grid item>
            <Typography className={classes.heading}>Details</Typography>
          </Grid>
          <Grid item style={{ marginLeft: "0.3rem" }}>
            <Typography className={classes.lineItemTitle}>
              - {timezoneFormat(container.created_at).split(",")[0]}
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>
          <Grid container spacing={2} className={classes.accordionContent}>
            <LineItem title="ETA:" value={container.eta} classes={classes} />
            <LineItem title="ATA:" value={container.ata} classes={classes} />
          </Grid>
          <Spacer height="2rem" />
          <Grid container spacing={2} className={classes.accordionContent}>
            <LineItem
              title="PO #:"
              value={container.po_number}
              classes={classes}
            />
            <LineItem
              title="BOL #:"
              value={container.bol_number}
              classes={classes}
            />
          </Grid>

          <Spacer height="2rem" />
          <Grid container>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                color="textPrimary"
                className={classes.notesTitle}
              >
                Notes:
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.notesContainer}>
              <Typography variant="body2" color="textPrimary">
                {container.notes}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function ProductsAccordion({ details, isProductsLoading }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  const hasProducts = Array.isArray(details) && details.length > 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.accordion}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="products-panel-content"
        id="products-panel-header"
      >
        <Typography className={classes.heading}>Products</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {isProductsLoading ? (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container spacing={2} className={classes.accordionContent}>
            {hasProducts ? (
              <>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    color="textPrimary"
                    className={classes.lineItemTitle}
                  >
                    SKU
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="subtitle2"
                    color="textPrimary"
                    className={classes.lineItemTitle}
                  >
                    Qty
                  </Typography>
                </Grid>
                {details.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        className={classes.lineItemValue}
                      >
                        {item.sku || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        className={classes.lineItemValue}
                      >
                        {item.qty || "-"}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2">No products</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function FilesAccordion({ container, onClose, onFileDelete }) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const layoutDispatch = useLayoutDispatch();
  const [deleteId, setDeleteId] = useState(null);

  const handleFileChange = ({ target: { files } }) => {
    const uploaded = [...uploadedFiles];
    const filesArray = [...files];
    filesArray
      .filter(
        (file) => uploadedFiles.findIndex((f) => f.name === file.name) === -1,
      )
      .map((file) => {
        uploaded.push(file);
        return file;
      });
    setUploadedFiles(filesArray);
  };

  const getExcelProducts = async (uploadedFiles) => {
    const promises = uploadedFiles.map((file) => {
      return new Promise((resolve) => {
        if (file.name.toLowerCase().includes("xlsx")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const wsname = workbook.SheetNames[0];
            const ws = workbook.Sheets[wsname];

            const rows = XLSX.utils.sheet_to_json(ws, {
              header: 1,
              raw: false,
              blankrows: false,
            });

            const normalizeHeader = (value) =>
              value?.toString().trim().toLowerCase();
            const headerAliases = {
              sku: ["sku"],
              quantity: ["quantity", "qty"],
            };

            const headerRowIndex = rows.findIndex((row) =>
              row.some((cell) => normalizeHeader(cell) === "sku"),
            );

            const columnIndexes = {};
            if (headerRowIndex !== -1) {
              rows[headerRowIndex].forEach((cell, idx) => {
                const normalized = normalizeHeader(cell);
                if (!normalized) return;

                Object.entries(headerAliases).forEach(([key, aliases]) => {
                  if (columnIndexes[key] !== undefined) return;
                  if (aliases.includes(normalized)) {
                    columnIndexes[key] = idx;
                  }
                });
              });
            }

            const getCellValue = (row, key) => {
              const columnIndex = columnIndexes[key];
              if (columnIndex === undefined) return undefined;
              const cell = row[columnIndex];
              if (typeof cell === "string") {
                return cell.trim();
              }
              return cell;
            };

            let values = [];
            if (headerRowIndex !== -1 && columnIndexes.sku !== undefined) {
              values = rows
                .slice(headerRowIndex + 1)
                .map((row) => ({
                  SKU: getCellValue(row, "sku"),
                  Quantity: getCellValue(row, "quantity"),
                }))
                .filter((row) => row.SKU && row.Quantity);
            }

            resolve(values);
          };
          reader.onerror = () => resolve([]);
          reader.readAsArrayBuffer(file);
        } else {
          resolve([]);
        }
      });
    });

    return Promise.all(promises).then((all) => all.flat());
  };

  const handleSubmit = async () => {
    const files = await toBase64Handler(uploadedFiles);
    const fileNames = uploadedFiles.map((file) => file.name);
    const fileTypes = [];
    fileNames.forEach((name) => {
      if (name.toLowerCase().includes(".pdf")) {
        fileTypes.push("PDF");
      } else {
        fileTypes.push("EXCEL");
      }
    });

    let products = await getExcelProducts(uploadedFiles);

    setIsLoading(true);
    createContainerFile({
      body: {
        files,
        fileNames,
        fileTypes,
        products,
        containerId: container.id,
      },
      responseSetter: (res) => {
        if (res.success) {
          intervalID = setInterval(() => {
            createContainerFile({
              body: {
                key: res.key,
              },
              responseSetter: (res) => {
                if (!res.success && res.message) {
                  setIsLoading(false);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "error",
                    message: res.message,
                  });
                  return clearInterval(intervalID);
                } else if (res.success) {
                  setIsLoading(false);
                  onClose(true);
                  setUploadedFiles([]);
                  popupNotification({
                    dispatch: layoutDispatch,
                    status: "success",
                    message: `Container file uploaded.`,
                  });
                  return clearInterval(intervalID);
                }
              },
            });
          }, 5000);
        } else {
          if (res.message) {
            popupNotification({
              dispatch: layoutDispatch,
              status: "error",
              message: res.message,
            });
          }
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      className={classes.accordion}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
      >
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography className={classes.heading}>Attachments</Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {container.files.map((file) => (
            <Grid item xs={12} className={classes.fileContainer}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Tooltip
                    title={`${file.file_name} - ${timezoneFormat(
                      file.created_at,
                    ).slice(0, 8)}`}
                    aria-label="hello"
                  >
                    <Typography variant="body2" color="textPrimary">
                      {file.file_name.slice(0, 26)}
                      {file.file_name.length > 26
                        ? `...${file.file_name.split(".")[1].toUpperCase()}`
                        : ""}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                          setDeleteId(file.id);
                        }}
                      >
                        <Trash color="red" size={0.8} />
                      </IconButton>
                    </Grid>
                    <Grid item>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                          window.open(file.signed_url, "_blank");
                        }}
                      >
                        <ExternalLink size={0.8} color="green" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
          {container.files.length > 0 && <Spacer height="1rem" />}
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <label htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    accept="application/pdf, .xlsx, .xls"
                    onChange={handleFileChange}
                    multiple
                    hidden
                  />
                  <Button
                    variant="contained"
                    size="medium"
                    color="primary"
                    component="span"
                    disabled={isLoading}
                  >
                    {uploadedFiles.length
                      ? uploadedFiles.length > 1
                        ? uploadedFiles.length + " Files"
                        : sliceText(uploadedFiles[0].name, 25)
                      : "Select File"}
                  </Button>
                </label>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={uploadedFiles.length === 0 || isLoading}
                  onClick={handleSubmit}
                >
                  {isLoading ? <CircularProgress size={20} /> : "Upload"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <DeleteFileModal
        file_id={deleteId}
        onClose={() => {
          setDeleteId(null);
        }}
        onDelete={onFileDelete}
      />
    </Accordion>
  );
}
